/* web-frontend/src/components/common/ConfirmModal.jsx */
// 🔥🔥🔥 [2025-12-22] secondaryButton 기능 추가 - 두 개의 확인 버튼 지원
// 🔥🔥🔥 [2025-12-24] ProfilePage 디자인 시스템과 통일 - CSS 변수 기반 스타일 적용
// [2026-07-02] 기본 텍스트 i18n 화 + dialog 접근성(role/aria/Escape/포커스 트랩) — FABLE P0-12/P1-21

import React, { useEffect, useRef } from 'react';
import './ConfirmModal.css';
import { X } from 'lucide-react';
import i18nService from '../../shared/services/i18n';

/**
 * ConfirmModal - 범용 확인 모달 컴포넌트
 * @param {boolean} isOpen - 모달 표시 여부
 * @param {function} onClose - 모달 닫기 핸들러
 * @param {function} onConfirm - 확인 버튼 클릭 핸들러
 * @param {string} title - 모달 제목
 * @param {string} message - 모달 메시지
 * @param {ReactNode} children - 커스텀 컨텐츠 (폼 요소 등)
 * @param {string} confirmText - 확인 버튼 텍스트 (미지정 시 현재 언어의 '확인')
 * @param {string|null} cancelText - 취소 버튼 텍스트 (미지정 시 현재 언어의 '취소', null 이면 버튼 숨김)
 * @param {boolean} isSubmitting - 로딩 상태 표시 (기본값: false)
 * @param {string|null} secondaryButtonText - 두 번째 확인 버튼 텍스트 (null이면 숨김)
 * @param {function|null} onSecondaryClick - 두 번째 확인 버튼 클릭 핸들러
 * @param {ReactNode|null} secondaryButtonIcon - 두 번째 버튼 아이콘
 * @param {string} variant - 모달 스타일 변형 ('default', 'warning', 'info')
 */
const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  children,
  confirmText,
  cancelText,
  isSubmitting = false,
  confirmDisabled = false,
  secondaryButtonText = null,
  onSecondaryClick = null,
  secondaryButtonIcon = null,
  variant = 'default',
  containerClassName = ''
}) => {
  const containerRef = useRef(null);
  const previousFocusRef = useRef(null);

  // 열릴 때 포커스 이동, 닫힐 때 이전 포커스 복원
  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement;
      containerRef.current?.focus();
      return () => {
        if (previousFocusRef.current && typeof previousFocusRef.current.focus === 'function') {
          previousFocusRef.current.focus();
        }
      };
    }
    return undefined;
  }, [isOpen]);

  // 모달이 닫혀있으면 렌더링하지 않음
  if (!isOpen) return null;

  // 미지정 시 현재 언어 기본값 사용. cancelText 는 명시적 null 이 "버튼 숨김" 시그널이므로 undefined 만 기본값 처리.
  const resolvedConfirmText = confirmText !== undefined ? confirmText : i18nService.t('common.confirm');
  const resolvedCancelText = cancelText === undefined ? i18nService.t('common.cancel') : cancelText;

  // 버튼이 하나만 있는지 확인 (확인 버튼만 표시할 때)
  const isSingleButton = resolvedCancelText === null && !secondaryButtonText;

  // 두 번째 버튼이 있는지 확인
  const hasSecondaryButton = secondaryButtonText && onSecondaryClick;

  // 버튼 레이아웃 클래스 결정
  const getActionsClass = () => {
    if (isSingleButton) return 'cm-actions single-button';
    if (hasSecondaryButton && resolvedCancelText === null) return 'cm-actions two-confirm-buttons';
    if (hasSecondaryButton) return 'cm-actions three-buttons';
    return 'cm-actions';
  };

  // 컨테이너 클래스명 생성
  const containerClasses = [
    'cm-container',
    variant !== 'default' ? `cm-${variant}` : '',
    containerClassName,
  ].filter(Boolean).join(' ');

  // 확인 버튼 클래스명 생성
  const primaryButtonClassName = `cm-btn-primary${isSingleButton ? ' full-width' : ''}`;

  // Escape 로 닫기 + Tab 포커스 트랩
  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && !isSubmitting) {
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key === 'Tab' && containerRef.current) {
      const focusables = containerRef.current.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  return (
    <div className="cm-overlay" onClick={onClose}>
      <div
        className={containerClasses}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'cm-modal-title' : undefined}
        ref={containerRef}
        tabIndex={-1}
      >
        {/* 닫기 버튼 */}
        <button
          className="cm-close-btn"
          onClick={onClose}
          disabled={isSubmitting}
          aria-label={i18nService.t('common.close')}
        >
          <X size={24} />
        </button>

        {/* 모달 본문 */}
        <div className="cm-body">
          {title && <h3 className="cm-title" id="cm-modal-title">{title}</h3>}
          {message && <p className="cm-message">{message}</p>}
          {children && <div className="cm-children-content">{children}</div>}
        </div>

        {/* 버튼 영역 */}
        <div className={getActionsClass()}>
          {/* 취소 버튼 (cancelText가 null이 아닐 때만) */}
          {resolvedCancelText !== null && (
            <button
              className="cm-btn-secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {resolvedCancelText}
            </button>
          )}

          {/* 두 번째 확인 버튼 (secondaryButtonText가 있을 때만) */}
          {hasSecondaryButton && (
            <button
              className="cm-btn-accent"
              onClick={onSecondaryClick}
              disabled={isSubmitting}
            >
              {secondaryButtonIcon && (
                <span className="cm-btn-icon">{secondaryButtonIcon}</span>
              )}
              {secondaryButtonText}
            </button>
          )}

          {/* 확인 버튼 */}
          <button
            className={primaryButtonClassName}
            onClick={onConfirm}
            disabled={isSubmitting || confirmDisabled}
          >
            {isSubmitting ? i18nService.t('common.processing') : resolvedConfirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
